import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';

interface DateConfig {
  label: string;
  price: number;
  image: string;
  date: string;
}

interface PurchaseStatus {
  approved: number;
  pending: number;
}

interface HistoryItem {
  id: number;
  date_key: string;
  date_label: string;
  amount: string;
  status: string;
  created_at: string;
}

const statusLabels: Record<string, string> = {
  pending: '待支付',
  approved: '已预约',
  failed: '失败',
  expired: '已过期',
  cancelled: '已取消',
};

const statusColors: Record<string, string> = {
  pending: 'text-yellow-600',
  approved: 'text-green-600',
  failed: 'text-red-500',
  expired: 'text-gray-400',
  cancelled: 'text-gray-400',
};

export default function ZhongyaoAppointment() {
  const [loading, setLoading] = useState(true);
  const [dates, setDates] = useState<Record<string, DateConfig> | null>(null);
  const [purchased, setPurchased] = useState<Record<string, PurchaseStatus>>({});
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [serverDate, setServerDate] = useState<string>('');

  useEffect(() => {
    api.get('/zhongyao/status').then(res => {
      setDates(res.data.dates);
      setPurchased(res.data.purchased || {});
      setServerDate(res.data.server_date || '');
    }).catch(() => {
      toast.error('加载失败');
    }).finally(() => setLoading(false));
  }, []);

  function handleOpenDate(key: string) {
    const dateConfig = dates?.[key];
    if (dateConfig && serverDate && serverDate < dateConfig.date) {
      toast('该日期预约尚未开放');
      return;
    }
    const status = purchased[key];
    if (status?.approved) {
      toast('您已预约该日期，不可重复预约');
      return;
    }
    if (status?.pending) {
      toast('您有待支付的订单，请稍候再试');
      return;
    }
    setSelectedDate(key);
    setShowPayModal(true);
  }

  async function handlePurchase(product: 'WX' | 'ALI' | 'ALI2') {
    if (!selectedDate || submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post('/zhongyao/purchase', { date_key: selectedDate, product });
      const payUrl = res.data?.data?.pay_url;
      if (payUrl) {
        window.location.href = payUrl;
      } else {
        toast.error('支付链接获取失败');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '预约失败');
    } finally {
      setSubmitting(false);
      setShowPayModal(false);
    }
  }

  async function loadHistory() {
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      const res = await api.get('/zhongyao/history');
      setHistory(res.data || []);
    } catch {
      toast.error('加载记录失败');
    } finally {
      setHistoryLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-md mx-auto min-h-screen flex items-center justify-center">
        <AuthGuard />
        <div className="text-gray-400 text-sm">加载中...</div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto min-h-screen relative">
      <AuthGuard />

      <div className="relative">
        <img src="/images/zhongyao_beijing.png" alt="" className="w-full" />
        <div className="absolute top-0 left-0 right-0 w-full" style={{ paddingTop: '520px' }}>
          <div className="px-4 pb-2">
            {/* Date/amount container frame */}
            <div className="relative">
              <img src="/images/zhongyao_efu.png" alt="" className="w-full" />
              <div className="absolute top-0 left-0 right-0 w-full" style={{ paddingTop: '72px' }}>
                <div className="space-y-2 px-4 pb-4">
                  {dates && Object.keys(dates).map(key => {
                    const dateConfig = dates[key];
                    const isApproved = purchased[key]?.approved > 0;
                    const isNotOpen = serverDate && dateConfig.date ? serverDate < dateConfig.date : false;
                    const isDisabled = isApproved || isNotOpen;

                    return (
                      <div key={key} className="relative">
                        <img
                          src={dateConfig.image}
                          alt={dateConfig.label}
                          className="w-full"
                          style={isDisabled ? { filter: 'grayscale(0.3)', opacity: 0.7 } : undefined}
                        />
                        <button
                          onClick={() => handleOpenDate(key)}
                          disabled={isDisabled}
                          className="absolute"
                          style={{
                            top: '15%',
                            right: '4%',
                            width: '28%',
                            height: '70%',
                            background: 'transparent',
                            border: 'none',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {isApproved && (
                            <span className="text-white text-xs font-bold bg-green-600 px-2 py-1 rounded-full">
                              已预约
                            </span>
                          )}
                          {!isApproved && isNotOpen && (
                            <span className="text-white text-xs font-bold bg-gray-500 px-2 py-1 rounded-full">
                              未开放
                            </span>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Success notice */}
          <div className="px-4 pb-2">
            <img src="/images/zhongyao_chenggong.png" alt="" className="w-full" />
          </div>

          {/* View history button */}
          <div className="px-4 pb-2">
            <button onClick={loadHistory} className="w-full">
              <img src="/images/zhongyao_chakan.png" alt="查看我的预约清单" className="w-full" />
            </button>
          </div>

          {/* Warning notice */}
          <div className="px-4 pb-8">
            <img src="/images/zhongyao_wenxin.png" alt="" className="w-full" />
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPayModal && selectedDate && dates && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => !submitting && setShowPayModal(false)}>
          <div className="bg-white rounded-2xl w-[85%] max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-center text-lg font-bold text-[#333] mb-1">
              大额提现预约 - {dates[selectedDate].label}
            </h3>
            <p className="text-center text-2xl font-bold text-[#c41a1a] mb-4">
              ¥{dates[selectedDate].price}
            </p>
            <div className="space-y-2.5">
              <button
                onClick={() => handlePurchase('WX')}
                disabled={submitting}
                className="w-full py-3 rounded-xl text-base font-bold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #07c160, #06ad56)' }}
              >
                {submitting ? '处理中...' : '微信支付'}
              </button>
              <button
                onClick={() => handlePurchase('ALI')}
                disabled={submitting}
                className="w-full py-3 rounded-xl text-base font-bold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #1677ff, #0958d9)' }}
              >
                {submitting ? '处理中...' : '支付宝支付'}
              </button>
              <button
                onClick={() => handlePurchase('ALI2')}
                disabled={submitting}
                className="w-full py-3 rounded-xl text-base font-bold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #1677ff, #0958d9)' }}
              >
                {submitting ? '处理中...' : '支付宝支付2'}
              </button>
            </div>
            <button
              onClick={() => setShowPayModal(false)}
              disabled={submitting}
              className="w-full mt-3 py-2 text-sm text-gray-400"
            >
              取消
            </button>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowHistory(false)}>
          <div className="bg-white rounded-2xl w-[90%] max-w-sm max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-100">
              <h3 className="text-center text-lg font-bold text-[#333]">我的预约清单</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {historyLoading ? (
                <div className="text-center text-gray-400 text-sm py-8">加载中...</div>
              ) : history.length === 0 ? (
                <div className="text-center text-gray-400 text-sm py-8">暂无记录</div>
              ) : (
                <div className="space-y-3">
                  {history.map(item => (
                    <div key={item.id} className="bg-gray-50 rounded-xl px-4 py-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-bold text-[#333]">{item.date_label}</span>
                        <span className={`text-xs font-bold ${statusColors[item.status] || 'text-gray-400'}`}>
                          {statusLabels[item.status] || item.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">{item.created_at}</span>
                        <span className="text-sm font-bold text-[#c41a1a]">¥{item.amount}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={() => setShowHistory(false)}
                className="w-full py-2.5 rounded-xl text-sm text-gray-500 bg-gray-100"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
